import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import ReportsLayout from '@Components/ReportsLayout';
import ScrollToTop from '@Components/ScrollToTop';
import Table, { TableHead } from '@Components/Table';
import CommonConstants from '@Constants/common.constants';
import { Icon } from '@Typings/global.types';
import {
  getRowsPerPageOptions,
  getSortingIcon,
} from '@Utils/common/common.utils';
import query from '@Utils/common/query.utils';
import useRoute from '@Utils/hooks/useRoute.hook';

import EmailLogTableProps from './EmailLogTable.types';

const EmailLogTable: FunctionComponent<EmailLogTableProps> = (
  props: EmailLogTableProps
): JSX.Element => {
  /** HOOKS */
  const { t } = useTranslation();
  const location = useLocation();
  const { changeRoute } = useRoute();

  /** PROPS */
  const { heading, tableProps, ...resProps } = props;
  const { loading, paginationProps, checked, setChecked, ...resTableProps } =
    tableProps;

  /** STATES */
  const [page, setPage] = useState<number>(CommonConstants.INITIAL_PAGE_NO - 1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(
    CommonConstants.INITIAL_ROWS_PER_PAGE
  );
  const [searchValue, setSearchValue] = useState<string>('');

  useEffect(() => {
    const searchParam =
      query.get(CommonConstants.QUERY_PARAMS.COMMON.SEARCH) || '';
    setSearchValue(`${searchParam}`);

    const pageNo =
      ((query.get(CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO) as number) ||
        CommonConstants.INITIAL_PAGE_NO) - 1;
    const pageSize =
      (query.get(CommonConstants.QUERY_PARAMS.COMMON.PAGE_SIZE) as number) ||
      CommonConstants.INITIAL_ROWS_PER_PAGE;

    setPage(pageNo);
    setRowsPerPage(pageSize);
  }, [location.search]);

  /** EDGE CASE
   * If the user performs an action and there are no more results to show
   * on the particular page then the user is redirected to the previous valid page.
   *
   * Set the pageNo to the previous valid page.
   */
  useEffect(() => {
    const newPageNo = Math.ceil(paginationProps.count / rowsPerPage);

    if (paginationProps.count && newPageNo < page + 1) {
      const pageNoParam = query.set(
        CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO,
        newPageNo
      );

      changeRoute(pageNoParam);
    }
  }, [paginationProps.count, page, rowsPerPage, changeRoute]);

  /** PAGE HEADING */
  const pageHeading = t('reports.Email Log Report');

  /** PAGINATION HANDLERS --> */
  const rowsPerPageOptions = getRowsPerPageOptions(
    CommonConstants.INITIAL_ROWS_PER_PAGE,
    paginationProps.count
  );

  /** HANDLES PAGE CHANGE */
  const handleChangePage = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ): void => {
    const searchParam = query.set(
      CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO,
      newPage + 1
    );
    changeRoute(searchParam);
  };

  /** HANDLES PAGE SIZE CHANGE */
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    const newRowsPerPage = parseInt(event.target.value, 10);

    const searchParams = query.setAll({
      [CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO]: 1,
      [CommonConstants.QUERY_PARAMS.COMMON.PAGE_SIZE]: newRowsPerPage,
    });
    changeRoute(searchParams);
  };
  /** <-- PAGINATION HANDLERS */

  const getTableHead = (): TableHead[] => {
    const orderParam = query.get(
      CommonConstants.QUERY_PARAMS.COMMON.ORDER
    ) as string;
    const directionParam = query.get(
      CommonConstants.QUERY_PARAMS.COMMON.DIRECTION
    ) as string;

    const handleTableHeadClick = (order: string) => {
      const param = query.setAll({
        [CommonConstants.QUERY_PARAMS.COMMON.ORDER]: order,
        ...(order === orderParam
          ? {
              [CommonConstants.QUERY_PARAMS.COMMON.DIRECTION]:
                directionParam === CommonConstants.INITIAL_SORT_DIRECTION
                  ? CommonConstants.QUERY_PARAMS.COMMON.SORT_BY.ASC
                  : CommonConstants.INITIAL_SORT_DIRECTION,
            }
          : {
              [CommonConstants.QUERY_PARAMS.COMMON.DIRECTION]:
                CommonConstants.INITIAL_SORT_DIRECTION,
            }),
      });

      changeRoute(param);
    };

    const getTableHeadIcon = (order: string): Icon => {
      if (order === orderParam) {
        return getSortingIcon(directionParam);
      }
      return 'sorting';
    };

    return [
      {
        label: t('reports.Sent At'),
        icon: getTableHeadIcon(CommonConstants.QUERY_PARAMS.COMMON.DATE_START),
        width: 20,
        onClick: () =>
          handleTableHeadClick(CommonConstants.QUERY_PARAMS.COMMON.DATE_START),
      },
      {
        label: t('reports.Email To'),
        width: 30,
      },
      {
        label: t('reports.Email'),
        width: 42,
      },
      {
        label: '',
        width: 8,
        align: 'center',
      },
    ];
  };

  /** ON SEARCH HANDLER
   * Sets query to search text and page No to 1
   */
  const onSearch = (value: string) => {
    const searchParam = query.setAll({
      [CommonConstants.QUERY_PARAMS.COMMON.SEARCH]: value,
      [CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO]: 1,
    });
    changeRoute(searchParam);
  };

  /** EMAIL LOG REPORT TABLE COMPONENT */
  const TableComponent = (
    <Table
      loading={loading}
      tableHead={getTableHead()}
      verticalAlign="initial"
      paginationProps={{
        page,
        rowsPerPage,
        rowsPerPageOptions,
        onPageChange: handleChangePage,
        onRowsPerPageChange: handleChangeRowsPerPage,
        ...paginationProps,
      }}
      {...resTableProps}
    />
  );
  /** <-- TABLE */

  return (
    <>
      <ScrollToTop
        dependencies={[
          query.get(CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO) || '',
          query.get(CommonConstants.QUERY_PARAMS.COMMON.PAGE_SIZE) || '',
        ]}
      />
      <ReportsLayout
        heading={heading || pageHeading}
        exportAllBtnProps={{ sx: { display: 'none' } }}
        searchProps={{
          value: searchValue,
          setValue: setSearchValue,
          onSearch,
        }}
        table={TableComponent}
        {...resProps}
      />
    </>
  );
};

export default EmailLogTable;
